import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { localToday } from '../utils/dateUtils';

const TIPOS = ['Audiencia', 'Consulta / Turno', 'Reunión'];

const colorTipo = (tipo) => {
  const map = { 'Audiencia': '#ef4444', 'Consulta / Turno': '#3b82f6', 'Reunión': '#8b5cf6' };
  return map[tipo] || '#9ca3af';
};

const formatDateShort = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
const formatDateLong  = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—';

function daysUntil(fecha, today) {
  return Math.round((new Date(fecha + 'T12:00:00') - new Date(today + 'T12:00:00')) / 86400000);
}

function UrgenciaChip({ days }) {
  if (days > 7) return null;
  const cfg = days === 0
    ? { label: 'Hoy', color: '#ef4444' }
    : days === 1
    ? { label: 'Mañana', color: '#f59e0b' }
    : { label: `En ${days} días`, color: '#f59e0b' };
  return (
    <span style={{ fontSize: 10, fontWeight: 600, color: cfg.color, background: cfg.color + '18', padding: '1px 7px', borderRadius: 10, flexShrink: 0 }}>
      {cfg.label}
    </span>
  );
}

function GrupoAudiencias({ fecha, items, today, onOpenEdit, onNavigateExpediente }) {
  const [open, setOpen] = useState(true);
  const days = daysUntil(fecha, today);
  return (
    <div style={{ border: '1px solid var(--border2)', borderRadius: 10, overflow: 'hidden' }}>
      <div onClick={() => setOpen(v => !v)}
        style={{ padding: '10px 16px', background: 'var(--surface2)', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', borderBottom: open ? '1px solid var(--border2)' : 'none' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', textTransform: 'capitalize', letterSpacing: 0.5 }}>{formatDateLong(fecha)}</span>
        <UrgenciaChip days={days} />
        <span style={{ fontSize: 11, color: 'var(--text4)', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 10, padding: '0 7px', marginLeft: 'auto' }}>{items.length}</span>
        <span style={{ fontSize: 11, color: 'var(--text4)' }}>{open ? '▼' : '▶'}</span>
      </div>
      {open && items.map(ev => {
        const color   = colorTipo(ev.tipo);
        const cliente = ev.clientes ? `${ev.clientes.apellido || ''} ${ev.clientes.nombre || ''}`.trim() : ev.cliente_manual;
        return (
          <div key={ev.id} onClick={() => onOpenEdit?.(ev)}
            style={{ padding: '10px 14px', display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer', borderBottom: '1px solid var(--border2)', background: 'var(--surface)' }}>
            <div style={{ width: 3, alignSelf: 'stretch', background: color, borderRadius: 2, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{ev.titulo}</span>
                {ev.hora && <span style={{ fontSize: 11, fontFamily: 'DM Mono', color }}>{ev.hora.substring(0, 5)}</span>}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 10, background: color + '18', color }}>{ev.tipo}</span>
                {ev.expedientes && (
                  <span onClick={e => { e.stopPropagation(); onNavigateExpediente?.(ev.expediente_id); }}
                    style={{ fontSize: 11, color: 'var(--blue)', cursor: 'pointer' }}>
                    {ev.expedientes.caratula?.substring(0, 35)}
                  </span>
                )}
                {cliente && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{cliente}</span>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Props:
//   expedienteId, clienteId  — filtros opcionales
//   mode: 'page' | 'widget'
//   limit — max items en widget
//   onOpenEdit(ev)
//   onNavigateExpediente(id)
export default function AudienciasPanel({
  expedienteId = null,
  clienteId = null,
  mode = 'page',
  limit = 6,
  onOpenEdit,
  onNavigateExpediente,
}) {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState('');

  const today = localToday();

  const fetchEventos = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('agenda')
      .select('*, clientes(nombre, apellido), expedientes(numero, caratula)')
      .in('tipo', TIPOS)
      .eq('estado', 'pendiente')
      .eq('eliminado', false)
      .gte('fecha', today)
      .order('fecha', { ascending: true })
      .order('hora', { ascending: true, nullsFirst: false });

    if (expedienteId) q = q.eq('expediente_id', expedienteId);
    if (clienteId)    q = q.eq('cliente_id', clienteId);

    const { data } = await q;
    setEventos(data || []);
    setLoading(false);
  }, [expedienteId, clienteId, today]);

  useEffect(() => { fetchEventos(); }, [fetchEventos]);

  const visibles = filtroTipo ? eventos.filter(e => e.tipo === filtroTipo) : eventos;

  // ── WIDGET ──────────────────────────────────────────────────────────────────
  if (mode === 'widget') {
    if (loading) return <div style={{ padding: '12px 0', color: 'var(--text4)', fontSize: 12 }}>Cargando…</div>;
    const items = visibles.slice(0, limit);
    if (items.length === 0) return <div style={{ padding: '12px 0', color: 'var(--text4)', fontSize: 12 }}>Sin audiencias próximas</div>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {items.map(ev => {
          const color = colorTipo(ev.tipo);
          const days  = daysUntil(ev.fecha, today);
          const cliente = ev.clientes ? `${ev.clientes.apellido || ''} ${ev.clientes.nombre || ''}`.trim() : ev.cliente_manual;
          return (
            <div key={ev.id} onClick={() => onOpenEdit?.(ev)}
              style={{ padding: '8px 11px', background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 8, borderLeft: `3px solid ${color}`, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.titulo}</div>
                <div style={{ fontSize: 10, color: 'var(--text4)', marginTop: 2 }}>
                  {formatDateShort(ev.fecha)}{ev.hora ? ` · ${ev.hora.substring(0,5)}` : ''}
                  {ev.expedientes && <span style={{ marginLeft: 6 }}>{ev.expedientes.caratula?.substring(0, 22)}</span>}
                  {!ev.expedientes && cliente && <span style={{ marginLeft: 6 }}>{cliente.substring(0, 22)}</span>}
                </div>
              </div>
              <UrgenciaChip days={days} />
            </div>
          );
        })}
      </div>
    );
  }

  // ── PAGE ─────────────────────────────────────────────────────────────────────
  const grupos = {};
  visibles.forEach(e => { (grupos[e.fecha] = grupos[e.fecha] || []).push(e); });
  const fechas = Object.keys(grupos).sort();

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: 'var(--text4)' }}>{visibles.length} próximas</span>
        <select className="filter-select" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={{ marginLeft: 'auto' }}>
          <option value="">Todos los tipos</option>
          {TIPOS.map(t => <option key={t}>{t}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text4)' }}>Cargando…</div>
      ) : fechas.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📅</div>
          <div className="empty-state-title">Sin audiencias próximas</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {fechas.map(fecha => (
            <GrupoAudiencias
              key={fecha}
              fecha={fecha}
              items={grupos[fecha]}
              today={today}
              onOpenEdit={onOpenEdit}
              onNavigateExpediente={onNavigateExpediente}
            />
          ))}
        </div>
      )}
    </div>
  );
}
